import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useReducer,
} from "react";

import { usePanel } from "../contexts/PanelContext";
import { useHasRole } from "../hooks/useHasRole";
import { KeybindManager } from "../lib/keybinds";
import { ModeratorModule } from "./ModeratorModule";
import { ModSidebar } from "./ModSidebar";
import { UserModSidebar } from "./UserModSidebar";

ModeratorModule.get(); // initialize

const context = createContext<{
  state: IModeratorContext;
  dispatch: React.ActionDispatch<[ModerationActions]>;
}>({
  state: {
    showMenu: false,
  },
  dispatch: () => {},
});

interface IModeratorContext {
  inspectUser?: string;
  showMenu: boolean;
  selection?: {
    selecting: boolean;
    point1?: [x: number, y: number];
    point2?: [x: number, y: number];
  };
}

type ModerationActions =
  | { action: "INSPECT_USER"; user?: string }
  | { action: "OPEN_MENU" }
  | { action: "CLOSE_MENU" }
  | { action: "START_SELECT_AREA" }
  | { action: "END_SELECT_AREA" }
  | { action: "SELECT_POINT_1"; x: number; y: number }
  | { action: "SELECT_POINT_2"; x: number; y: number };

export const useModerator = () => useContext(context);

export const ModeratorContext = ({ children }: React.PropsWithChildren) => {
  const isMod = useHasRole("MOD");
  const Panel = usePanel();

  const [state, reducerDispatch] = useReducer<IModeratorContext, [ModerationActions]>(
    (state, data) => {
      switch (data.action) {
        case "INSPECT_USER":
          return {
            ...state,
            inspectUser: data.user,
            showMenu: data.user ? false : state.showMenu,
          };
        case "OPEN_MENU":
          return {
            ...state,
            inspectUser: undefined,
            showMenu: true,
          };
        case "CLOSE_MENU":
          return {
            ...state,
            showMenu: false,
          };
        case "START_SELECT_AREA":
          return {
            ...state,
            selection: {
              selecting: true,
              // intentional overwrite of existing properties
            },
          };
        case "END_SELECT_AREA":
          return {
            ...state,
            selection: {
              selecting: false,
              // intentional overwrite of existing properties
            },
          };
        case "SELECT_POINT_1":
          return {
            ...state,
            selection: {
              selecting: true,
              point1: [data.x, data.y],
              point2: undefined,
            },
          };
        case "SELECT_POINT_2":
          return {
            ...state,
            selection: {
              ...state.selection,
              selecting: true,
              point2: [data.x, data.y],
            },
          };
      }

      throw new Error(
        "Unknown ModeratorContext action: " + JSON.stringify(data)
      );
    },
    {
      inspectUser: undefined,
      showMenu: false,
    }
  );

  const dispatch = useCallback(
    (action: ModerationActions) => {
      if (
        action.action === "OPEN_MENU" ||
        (action.action === "INSPECT_USER" && action.user)
      ) {
        Panel.dispatch(["info", undefined]);
      }
      reducerDispatch(action);
    },
    [Panel.dispatch],
  );

  useLayoutEffect(() => {
    if (!Panel.state.info) return;

    if (state.showMenu) {
      reducerDispatch({ action: "CLOSE_MENU" });
    } else if (state.inspectUser) {
      reducerDispatch({ action: "INSPECT_USER" });
    }
  }, [Panel.state.info, state.inspectUser, state.showMenu]);

  useEffect(() => {
    if (isMod) {
      ModeratorModule.get().connect();
    } else {
      ModeratorModule.get().socket.disconnect();
    }

    const handleKeybind = () => {
      if (!isMod) {
        console.warn("TOGGLE_MOD_MENU canceled, user is not a moderator");
        return;
      }
      dispatch({ action: "OPEN_MENU" });
    };

    KeybindManager.on("TOGGLE_MOD_MENU", handleKeybind);

    return () => {
      KeybindManager.off("TOGGLE_MOD_MENU", handleKeybind);
    };
  }, [isMod]);

  return (
    <context.Provider value={{ state, dispatch }}>{children}</context.Provider>
  );
};

export const Moderator = () => {
  return (
    <>
      <ModSidebar />
      <UserModSidebar />
    </>
  );
};

interface AdminUIUrls {
  ip_lookup: [address: string];
  user: [sub: string];
}

export class ModeratorUtils {
  static url<T extends keyof AdminUIUrls>(
    which: T,
    ...args: AdminUIUrls[T]
  ): string {
    switch (which) {
      case "ip_lookup":
        return `/admin/accounts?filter[ip]=${encodeURIComponent(args[0])}`;
      case "user":
        return `/admin/accounts/${encodeURIComponent(args[0])}`;
    }

    throw new Error("Unknown URL: " + which);
  }
}
